/**
 * Mobile optimization utilities for enhanced mobile experience
 */

// Viewport and device detection
export class ViewportUtils {
  /**
   * Get current viewport dimensions
   */
  static getViewportSize(): { width: number; height: number } {
    return {
      width: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
      height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
    };
  }

  /**
   * Check if device is in landscape mode
   */
  static isLandscape(): boolean {
    return window.innerWidth > window.innerHeight;
  }

  /**
   * Get safe area insets for devices with notches
   */
  static getSafeAreaInsets(): {
    top: number;
    right: number;
    bottom: number;
    left: number;
  } {
    const style = getComputedStyle(document.documentElement);
    return {
      top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0'),
      right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0'),
      bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
      left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0')
    };
  }

  /**
   * Optimize viewport for mobile devices
   */
  static optimizeViewport(): void {
    // Prevent zoom on input focus (iOS)
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }

    // Add viewport height CSS custom property
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      setTimeout(setVH, 100); // Delay to ensure accurate measurement
    });
  }
}

// Touch and gesture handling
export class TouchGestureUtils {
  private static touchStartY = 0;
  private static touchStartX = 0;

  /**
   * Prevent overscroll/bounce effect on iOS
   */
  static preventOverscroll(): void {
    document.addEventListener('touchstart', (e) => {
      this.touchStartY = e.touches[0].clientY;
      this.touchStartX = e.touches[0].clientX;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const deltaY = touchY - this.touchStartY;
      const deltaX = touchX - this.touchStartX;

      // Check if we're at the top or bottom of the page
      const isAtTop = window.scrollY <= 0;
      const isAtBottom = window.scrollY >= document.documentElement.scrollHeight - window.innerHeight;

      // Prevent overscroll
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault();
      }

      // Prevent horizontal overscroll
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Add swipe gesture detection
   */
  static addSwipeGesture(
    element: HTMLElement,
    callbacks: {
      onSwipeLeft?: () => void;
      onSwipeRight?: () => void;
      onSwipeUp?: () => void;
      onSwipeDown?: () => void;
    },
    threshold = 50
  ): () => void {
    let startX = 0;
    let startY = 0;
    let startTime = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const endTime = Date.now();

      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const deltaTime = endTime - startTime;

      // Only consider it a swipe if it's fast enough and long enough
      if (deltaTime > 500 || (Math.abs(deltaX) < threshold && Math.abs(deltaY) < threshold)) {
        return;
      }

      // Determine swipe direction
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Horizontal swipe
        if (deltaX > 0) {
          callbacks.onSwipeRight?.();
        } else {
          callbacks.onSwipeLeft?.();
        }
      } else {
        // Vertical swipe
        if (deltaY > 0) {
          callbacks.onSwipeDown?.();
        } else {
          callbacks.onSwipeUp?.();
        }
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }

  /**
   * Add pull-to-refresh functionality
   */
  static addPullToRefresh(
    element: HTMLElement,
    onRefresh: () => Promise<void>,
    threshold = 80
  ): () => void {
    let startY = 0;
    let currentY = 0;
    let isPulling = false;
    let refreshTriggered = false;

    const indicator = document.createElement('div');
    indicator.className = 'pull-to-refresh-indicator';
    indicator.innerHTML = '↓ Pull to refresh';
    indicator.style.cssText = `
      position: fixed;
      top: -60px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px 20px;
      border-radius: 20px;
      font-size: 14px;
      z-index: 1000;
      transition: top 0.3s ease;
    `;
    document.body.appendChild(indicator);

    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        isPulling = true;
        refreshTriggered = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;

      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      if (deltaY > 0 && window.scrollY === 0) {
        e.preventDefault();
        const progress = Math.min(deltaY / threshold, 1);
        indicator.style.top = `${-60 + (progress * 80)}px`;
        
        if (deltaY > threshold && !refreshTriggered) {
          indicator.innerHTML = '↑ Release to refresh';
          refreshTriggered = true;
        } else if (deltaY <= threshold && refreshTriggered) {
          indicator.innerHTML = '↓ Pull to refresh';
          refreshTriggered = false;
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPulling && refreshTriggered) {
        indicator.innerHTML = '⟳ Refreshing...';
        indicator.style.top = '20px';
        
        onRefresh().finally(() => {
          indicator.style.top = '-60px';
          setTimeout(() => {
            indicator.innerHTML = '↓ Pull to refresh';
          }, 300);
        });
      } else {
        indicator.style.top = '-60px';
      }
      
      isPulling = false;
      refreshTriggered = false;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      document.body.removeChild(indicator);
    };
  }
}

// Performance optimization for mobile
export class MobilePerformanceUtils {
  /**
   * Optimize images for mobile devices
   */
  static optimizeImages(): void {
    const images = document.querySelectorAll('img');
    
    images.forEach(img => {
      // Add loading="lazy" if not already present
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }

      // Add decoding="async" for better performance
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }

      // Optimize image quality based on device pixel ratio
      const dpr = window.devicePixelRatio || 1;
      if (dpr > 1 && img.src && !img.src.includes('@2x')) {
        // Try to load high-DPI version if available
        const highDpiSrc = img.src.replace(/\.(jpg|jpeg|png|webp)$/i, '@2x.$1');
        const testImg = new Image();
        testImg.onload = () => {
          img.src = highDpiSrc;
        };
        testImg.src = highDpiSrc;
      }
    });
  }

  /**
   * Reduce animations on low-end devices
   */
  static optimizeAnimations(): void {
    // Detect low-end devices (simplified heuristic)
    const isLowEndDevice = navigator.hardwareConcurrency <= 2 || 
                          (navigator as any).deviceMemory <= 2 ||
                          /Android.*Chrome\/[0-5]/.test(navigator.userAgent);

    if (isLowEndDevice) {
      document.documentElement.classList.add('low-end-device');
      
      // Add CSS to reduce animations
      const style = document.createElement('style');
      style.textContent = `
        .low-end-device * {
          animation-duration: 0.1s !important;
          transition-duration: 0.1s !important;
        }
        .low-end-device .hero-section::before,
        .low-end-device .hero-section::after {
          animation: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Optimize font loading for mobile
   */
  static optimizeFonts(): void {
    // Preload critical fonts
    const criticalFonts = [
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto'
    ];

    // Use font-display: swap for better performance
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'Roboto';
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Implement virtual scrolling for large lists
   */
  static createVirtualScroller(
    container: HTMLElement,
    items: any[],
    itemHeight: number,
    renderItem: (item: any, index: number) => HTMLElement
  ): void {
    const viewportHeight = container.clientHeight;
    const visibleCount = Math.ceil(viewportHeight / itemHeight) + 2; // Buffer
    let scrollTop = 0;
    let startIndex = 0;

    const scrollContent = document.createElement('div');
    scrollContent.style.height = `${items.length * itemHeight}px`;
    scrollContent.style.position = 'relative';

    const visibleContent = document.createElement('div');
    visibleContent.style.position = 'absolute';
    visibleContent.style.top = '0';
    visibleContent.style.width = '100%';

    scrollContent.appendChild(visibleContent);
    container.appendChild(scrollContent);

    const updateVisibleItems = () => {
      startIndex = Math.floor(scrollTop / itemHeight);
      const endIndex = Math.min(startIndex + visibleCount, items.length);

      visibleContent.innerHTML = '';
      visibleContent.style.transform = `translateY(${startIndex * itemHeight}px)`;

      for (let i = startIndex; i < endIndex; i++) {
        const itemElement = renderItem(items[i], i);
        itemElement.style.height = `${itemHeight}px`;
        visibleContent.appendChild(itemElement);
      }
    };

    container.addEventListener('scroll', () => {
      scrollTop = container.scrollTop;
      requestAnimationFrame(updateVisibleItems);
    });

    updateVisibleItems();
  }
}

// Network optimization for mobile
export class NetworkOptimizationUtils {
  /**
   * Detect connection quality
   */
  static getConnectionQuality(): 'slow' | 'fast' | 'unknown' {
    const connection = (navigator as any).connection || 
                     (navigator as any).mozConnection || 
                     (navigator as any).webkitConnection;

    if (!connection) return 'unknown';

    const slowConnections = ['slow-2g', '2g', '3g'];
    return slowConnections.includes(connection.effectiveType) ? 'slow' : 'fast';
  }

  /**
   * Preload critical resources based on connection
   */
  static preloadCriticalResources(): void {
    const connectionQuality = this.getConnectionQuality();
    
    if (connectionQuality === 'fast') {
      // Preload images and other resources
      const criticalImages = document.querySelectorAll('[data-preload]');
      criticalImages.forEach(img => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = img.getAttribute('data-preload') || '';
        document.head.appendChild(link);
      });
    }
  }

  /**
   * Implement adaptive loading based on connection
   */
  static adaptiveLoading(): void {
    const connectionQuality = this.getConnectionQuality();
    
    if (connectionQuality === 'slow') {
      // Reduce image quality
      document.documentElement.classList.add('slow-connection');
      
      // Add CSS for low-quality images
      const style = document.createElement('style');
      style.textContent = `
        .slow-connection img {
          filter: blur(0.5px);
          image-rendering: optimizeSpeed;
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Battery optimization
export class BatteryOptimizationUtils {
  /**
   * Optimize based on battery level
   */
  static async optimizeForBattery(): Promise<void> {
    if ('getBattery' in navigator) {
      try {
        const battery = await (navigator as any).getBattery();
        
        if (battery.level < 0.2 || !battery.charging) {
          // Low battery mode
          document.documentElement.classList.add('low-battery');
          
          // Reduce animations and effects
          const style = document.createElement('style');
          style.textContent = `
            .low-battery * {
              animation: none !important;
              transition: none !important;
            }
            .low-battery .hero-section::before,
            .low-battery .hero-section::after {
              display: none !important;
            }
          `;
          document.head.appendChild(style);
        }
      } catch (error) {
        console.log('Battery API not supported');
      }
    }
  }
}

// Comprehensive mobile optimization initialization
export const initializeMobileOptimizations = (): void => {
  // Viewport optimization
  ViewportUtils.optimizeViewport();
  
  // Touch and gesture optimization
  TouchGestureUtils.preventOverscroll();
  
  // Performance optimization
  MobilePerformanceUtils.optimizeImages();
  MobilePerformanceUtils.optimizeAnimations();
  MobilePerformanceUtils.optimizeFonts();
  
  // Network optimization
  NetworkOptimizationUtils.preloadCriticalResources();
  NetworkOptimizationUtils.adaptiveLoading();
  
  // Battery optimization
  BatteryOptimizationUtils.optimizeForBattery();
};